import React, {useState} from 'react';
import Slider, {LazyLoadTypes} from 'react-slick';

import {
  Breakpoints,
  CustomImage,
  Text,
  TextAppearance,
  TextTag,
  useIsMobile,
} from '@vw-marketing/us-components';

import {AppConstants} from '../../utils/app-constants';
import {getGalleryImagesUrl} from '../../utils/general';
import {
  StyledImageGallery,
  StyledSlickContainer,
  StyledSliderCount,
} from './styled';
import {UsedVehicle} from '../../typing/used-inventory';
import {useTranslations} from '../../hooks/use-translations';

interface ImageGalleryProps {
  vehicle: UsedVehicle;
}

export const ImageGallery = ({vehicle}: ImageGalleryProps): JSX.Element => {
  const noFoundImage = AppConstants.NoVehicleImage;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [gallery, setGallery] = useState<Slider>();
  const [galleryNav, setGalleryNav] = useState<Slider>();
  const isMobile = useIsMobile(Breakpoints.b960);
  const translations = useTranslations().detailsSection.gallery;
  const galleryImages = getGalleryImagesUrl(vehicle);
  const countLabel = translations.imagesCountLabel
    ?.replace('%%current%%', (currentSlide + 1) as any)
    ?.replace('%%totalElements%%', galleryImages.length as any);

  const settingsGallery = {
    dots: false,
    arrows: !isMobile,
    infinite: false,
    centerMode: false,
    adaptiveHeight: false,
    swipe: isMobile,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    asNavFor: galleryNav,
    lazyLoad: 'ondemand' as LazyLoadTypes,
    prevArrow: <span />,
    nextArrow: <span />,
    afterChange: current => setCurrentSlide(current),
  };

  const settingsGalleryNav = {
    dots: false,
    arrows: false,
    infinite: false,
    swipe: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    focusOnSelect: true,
    asNavFor: gallery,
    lazyLoad: 'ondemand' as LazyLoadTypes,
  };

  return (
    <StyledImageGallery className="image-gallery">
      {galleryImages.length > 1 ? (
        <>
          <StyledSlickContainer className="gallery-carousel">
            <Slider
              {...settingsGallery}
              ref={slider1 => setGallery(slider1 ? slider1 : undefined)}
            >
              {galleryImages.map((imageUrl: string, index: number) => (
                <CustomImage
                  key={index}
                  alt={vehicle.model || ''}
                  srcPlaceholder={AppConstants.PlaceHolder960x432}
                  srcErrorImage={noFoundImage}
                  src={imageUrl}
                />
              ))}
            </Slider>
            <StyledSliderCount className="slider-count">
              <Text
                tag={TextTag.span}
                appearance={TextAppearance.label100}
                bold
              >
                {countLabel}
              </Text>
            </StyledSliderCount>
          </StyledSlickContainer>

          <StyledSlickContainer isNav={true} className="gallery-nav-carousel">
            <Slider
              {...settingsGalleryNav}
              ref={slider2 => setGalleryNav(slider2 ? slider2 : undefined)}
            >
              {galleryImages.map((imageUrl: string, index: number) => (
                <CustomImage
                  key={index}
                  alt={vehicle.model || ''}
                  srcPlaceholder={AppConstants.PlaceHolder960x432}
                  srcErrorImage={noFoundImage}
                  src={imageUrl}
                />
              ))}
            </Slider>
          </StyledSlickContainer>
        </>
      ) : (
        <CustomImage
          alt={vehicle.model || ''}
          srcPlaceholder={AppConstants.PlaceHolder960x432}
          srcErrorImage={noFoundImage}
          src={vehicle.mediaImageUrl || ''}
        />
      )}
    </StyledImageGallery>
  );
};
